import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStripeCheckout } from '../../hooks/useStripe';
import SafeIcon from '../../common/SafeIcon';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiTag, FiCheck, FiX } = FiIcons;

const CouponInput = ({ coursePrice, onCouponApplied, onCouponRemoved }) => {
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { validateCoupon } = useStripeCheckout();

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setLoading(true);
    setError('');

    try {
      const result = await validateCoupon(couponCode.trim(), coursePrice);
      
      if (result.is_valid) {
        const couponData = {
          code: couponCode.trim(),
          discountAmount: result.discount_amount
        };
        setAppliedCoupon(couponData);
        onCouponApplied(couponData);
        toast.success('Coupon applied successfully!');
      } else {
        setError(result.error_message);
        toast.error(result.error_message);
      }
    } catch (error) {
      setError('Failed to validate coupon');
      toast.error('Failed to validate coupon');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setError('');
    onCouponRemoved();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyCoupon();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <SafeIcon icon={FiTag} className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">Have a coupon code?</span>
      </div>

      <AnimatePresence mode="wait">
        {!appliedCoupon ? (
          <motion.div
            key="input"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2"
          >
            <div className="flex space-x-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                placeholder="Enter coupon code"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                disabled={loading}
              />
              <button
                onClick={handleApplyCoupon}
                disabled={!couponCode.trim() || loading}
                className="px-4 py-2 bg-nutrition-600 text-white rounded-md text-sm font-medium hover:bg-nutrition-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? <LoadingSpinner size="sm" /> : 'Apply'}
              </button>
            </div>
            
            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-600 text-xs"
              >
                {error}
              </motion.p>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="applied"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md"
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">
                {appliedCoupon.code} applied
              </span>
              <span className="text-xs text-green-600">
                (-${appliedCoupon.discountAmount.toFixed(2)})
              </span>
            </div>
            <button
              onClick={handleRemoveCoupon}
              className="text-green-600 hover:text-green-800 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CouponInput;